/**
 * Paint REST, upgrade to live — ARCHITECTURE.md §4.8a, ROADMAP 3.16.
 *
 * Runs IN PARALLEL with the websocket subscribe, never before it:
 *
 *   switch ─┬─ REST /market/quotes ──► ~50-150 ms ──► paint, marked SNAPSHOT
 *           └─ WS subscribe         ──► ~50-200 ms ──► upgrade, clear mark
 *
 * Whichever lands first wins, and the store refuses to let a late snapshot
 * overwrite a live value — a snapshot arriving after the first tick would
 * move the price BACKWARDS by however long the HTTP call took.
 *
 * A failed snapshot is not a failed screen: the cells simply stay dashed for
 * a moment longer, which is exactly the behaviour before this existed.
 */

import { useEffect, useRef } from 'react'
import { api, qs } from '@/lib/api'
import { TICK_SOURCE, tickStore } from './tickStore'
import { tickSocket } from './tickSocket'

interface QuoteDto {
  /** 'CLOSE' only ever comes back outside market hours. */
  source?: 'LIVE' | 'SIMULATION' | 'CLOSE'
  token: number
  ltp: number
  bid: number
  ask: number
  open: number
  high: number
  low: number
  close: number
  oi: number
  exchTs: number
}

/** The server refuses more than this; asking is a wasted round trip. */
const MAX_TOKENS = 250

export async function fetchSnapshot(tokens: number[]): Promise<number> {
  if (tokens.length === 0) return 0
  const epoch = tickStore.marketEpoch()
  const res = await api.get<{ items: QuoteDto[]; at: number }>(
    `/market/quotes${qs({ tokens: tokens.slice(0, MAX_TOKENS).join(',') })}`,
  )
  if (epoch !== tickStore.marketEpoch()) return 0
  let painted = 0
  for (const q of res.items) {
    if ((q.source === 'SIMULATION') !== (tickSocket.getMarketContext().source === 'SIMULATION')) continue
    const ok = tickStore.applySnapshot(
      q.token,
      {
        ltp: q.ltp,
        bid: q.bid,
        ask: q.ask,
        open: q.open,
        high: q.high,
        low: q.low,
        close: q.close,
        oi: q.oi,
        exchTs: q.exchTs,
      },
      res.at,
      q.source === 'CLOSE'
        ? TICK_SOURCE.CLOSE
        : q.source === 'SIMULATION'
          ? TICK_SOURCE.SIMULATION
          : TICK_SOURCE.SNAPSHOT,
    )
    if (ok) painted += 1
  }
  return painted
}

/**
 * Snapshot a set of tokens once, when the set changes.
 *
 * Keyed on CONTENTS rather than array identity, like `useTickSubscription`,
 * so a caller building the list inline does not refetch every render.
 *
 * Only the tokens with no data yet are requested. Re-snapshotting a cell that
 * is already streaming would spend a rate-limited broker call ([B10]) to
 * learn something older than what is on screen.
 */
export function useSnapshot(tokens: readonly number[]): void {
  const key = tokens.join(',')
  const inFlight = useRef<string | null>(null)

  useEffect(() => {
    if (tokens.length === 0) return
    if (inFlight.current === key) return
    inFlight.current = key

    const missing = tokens.filter((t) => {
      const slot = tickStore.slotFor(t)
      return slot < 0 || tickStore.field(slot, 'ltp') === 0
    })
    if (missing.length === 0) return

    let cancelled = false
    void fetchSnapshot(missing).catch(() => {
      // Deliberately silent to the user. The live feed is the real source and
      // this only shortens the blank window; a toast for a slow convenience
      // call would be noise during the one moment they are reading prices.
      if (!cancelled) inFlight.current = null
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
}
